import {
  ChangeEvent,
  DragEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FileUploadedStatus, InputUploadFileProps } from "src/types";
import { useBoolean } from "src/utils";

export const useInputUploadFile = ({
  selectedFile,
  maxFileSizeMB = 1,
  onFileSelected,
  onRemove,
  fileStatus,
}: InputUploadFileProps) => {
  const { t } = useTranslation();
  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

  const [isShowAlert, , , toggleAlert] = useBoolean(false);
  const [file, setFile] = useState<File | undefined>(selectedFile);
  const [isOutOfSizeFile, setIsOutOfSizeFile] = useState(false);

  const inputUploadFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (file) onFileSelected(file);
  }, [file, onFileSelected]);

  const getIsOverSizeFile = (file: File) => {
    const filesSize = file.size;
    if (filesSize > maxFileSizeBytes) {
      setIsOutOfSizeFile(true);
      return true;
    }
    return false;
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files ? event.target.files[0] : undefined;
    if (selectedFile && getIsOverSizeFile(selectedFile)) return;

    setFile(selectedFile);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    const droppedFile = event.dataTransfer.files[0];
    if (getIsOverSizeFile(droppedFile)) return;

    setFile(droppedFile);
  };

  const handleRemoveFile = () => {
    setFile(undefined);
    toggleAlert();
    if (inputUploadFileRef.current) inputUploadFileRef.current.value = "";
  };

  const confirmAction = () => {
    handleRemoveFile();
    if (onRemove) {
      onRemove();
    }
  };

  const fileStatusMessage = useMemo(() => {
    switch (fileStatus) {
      case FileUploadedStatus.OK:
        return "upload_file.upload_success";
      case FileUploadedStatus.PARTIAL_ERRORS:
        return "upload_file.upload_partial_errors";
      case FileUploadedStatus.ERRORS:
        return "upload_file.upload_errors_title";
      case FileUploadedStatus.LIMIT_EXCEEDED:
        return "upload_file.upload_limit_exceeded_error_title";
      case FileUploadedStatus.EMPTY:
        return "upload_file.upload_empty_error";
      case FileUploadedStatus.FORMAT_ERROR:
        return "upload_file.upload_format_error";
      default:
        return "";
    }
  }, [fileStatus]);

  return {
    t,
    file,
    fileStatusMessage,
    isOutOfSizeFile,
    isShowAlert,
    inputUploadFileRef,
    toggleAlert,
    setIsOutOfSizeFile,
    handleDrop,
    handleFileChange,
    confirmAction,
  };
};
